import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axiosInstant from '../../api'
import { productAutopilotEndpoint } from '../../api/endpoints'
import { ProductAutopilot } from '../../types/autopilot'
import { ObjectiveProposalFlow } from '../../components/ProposalFlow/ObjectiveProposalFlow'
import { ProposalDisclaimer } from '../../components/ProposalFlow/ProposalDisclaimer'
import { ProposalProductInput, WizardProductRef } from '../../components/ProposalFlow/types'
import { FONT, PAGE_BG } from '../../components/ProposalFlow/theme'
import { useUser } from '../../hooks/useUser'

// Portal proposal wizard — same shell as the public page
// (src/pages/publicProposal/ProposalPage.tsx) minus the ?bg=/embed handling
// and captcha: the portal is behind auth and the wizard runs against the
// rmnControl-scoped /rmn/proposals/* endpoints via the `rmn` prop. Mounted
// both on the legacy white-label route (/portal/:brandSlug/proposal) and
// slug-free at /opportunities/strategies/new (TDC-1499). Product context can
// still arrive via query params (e.g. from the PDP audit report's "Create
// Proposal" CTA): ?title=&url=&image=&brand=&category=&audit=
// Product LINKS arrive as ?desireid= (imported) or ?dp= (discovered staging
// row — auto-imported at create) and pre-seed the Step-1 product picker.

export default function RmnProposalPage() {
  const { brandSlug } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useUser()

  // PDP Autopilot (TDC-1646): a recommendation's "Add to strategy" opens
  // the wizard with ?recommendation_ids=1,2&desireid=&pdp_url_id=. The
  // objective comes from the product's autopilot bundle (the
  // recommendation's own objective); the ids ride on the create call.
  const recommendationIds = useMemo<number[]>(() => {
    const raw = searchParams.get('recommendation_ids') || ''
    return raw
      .split(',')
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0)
  }, [searchParams])
  const pdpUrlId = useMemo(() => {
    const raw = Number(searchParams.get('pdp_url_id'))
    return Number.isInteger(raw) && raw > 0 ? raw : undefined
  }, [searchParams])
  const [autopilot, setAutopilot] = useState<ProductAutopilot | null>(null)
  useEffect(() => {
    const desireid = Number(searchParams.get('desireid'))
    if (!recommendationIds.length || !(Number.isInteger(desireid) && desireid > 0)) return undefined
    let cancelled = false
    axiosInstant
      .get<ProductAutopilot>(productAutopilotEndpoint(desireid))
      .then(({ data }) => {
        if (!cancelled) setAutopilot(data)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [recommendationIds, searchParams])
  const initialObjectiveId = useMemo<number | null>(() => {
    if (!autopilot || !recommendationIds.length) return null
    const rec = autopilot.recommendations.find((r) => recommendationIds.indexOf(r.id) >= 0 && r.objective)
    return rec && rec.objective ? rec.objective.id : null
  }, [autopilot, recommendationIds])
  const autopilotPageUrl = useMemo(() => {
    if (!autopilot || !pdpUrlId) return undefined
    const page = autopilot.pages.find((pg) => pg.id === pdpUrlId)
    return page ? page.pdp_url : undefined
  }, [autopilot, pdpUrlId])

  const product = useMemo<ProposalProductInput | undefined>(() => {
    const title = searchParams.get('title') || undefined
    const url = searchParams.get('url') || autopilotPageUrl || undefined
    const image = searchParams.get('image') || undefined
    const brandName = searchParams.get('brand') || undefined
    if (!title && !url) return undefined
    return { title, url, image, brand_name: brandName }
  }, [searchParams, autopilotPageUrl])

  const defaultCategoryId = useMemo(() => {
    const raw = Number(searchParams.get('category'))
    return Number.isInteger(raw) && raw > 0 ? raw : undefined
  }, [searchParams])

  const initialProducts = useMemo<WizardProductRef[] | undefined>(() => {
    const desireid = Number(searchParams.get('desireid'))
    const dp = Number(searchParams.get('dp'))
    const title = searchParams.get('title') || undefined
    const image = searchParams.get('image') || undefined
    const url = searchParams.get('url') || undefined
    if (Number.isInteger(desireid) && desireid > 0) {
      return [{ kind: 'desire', desireid, title: title || `Product #${desireid}`, image, url }]
    }
    if (Number.isInteger(dp) && dp > 0) {
      return [{ kind: 'discovered', id: dp, title: title || `Product #${dp}`, image, url }]
    }
    return undefined
  }, [searchParams])

  const pdpAuditReportId = useMemo(() => {
    const raw = Number(searchParams.get('audit'))
    return Number.isInteger(raw) && raw > 0 ? raw : undefined
  }, [searchParams])

  // A catalog product's latest PDP score (Products page's Create Strategy).
  const pdpUrlScoreId = useMemo(() => {
      const raw = Number(searchParams.get('score'))
      return Number.isInteger(raw) && raw > 0 ? raw : undefined
  }, [searchParams])

  // A Consumer Voice analysis (its report view's Create strategy CTA);
  // anonymous rows also carry their access token.
  const consumerVoiceAuditId = useMemo(() => {
      const raw = Number(searchParams.get("cv"));
      return Number.isInteger(raw) && raw > 0 ? raw : undefined;
  }, [searchParams]);
  // Or an AI Presence analysis (same CTA pattern).
  const aiPresenceAuditId = useMemo(() => {
      const raw = Number(searchParams.get('aip'));
      return Number.isInteger(raw) && raw > 0 ? raw : undefined;
  }, [searchParams]);
  // Anonymous Consumer Voice / AI Presence rows carry their access token.
  const intelAccessToken = searchParams.get("t") || undefined;

  const storeEmail =
    ((user as any)?.emailAddress as string | undefined) ||
    ((user as any)?.email_address as string | undefined) ||
    ''

  return (
    <div style={{ flex: 1, fontFamily: FONT, background: PAGE_BG, minHeight: '100%' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
      <div style={{ width: 960, maxWidth: '100%', margin: '0 auto', padding: '48px 20px' }}>
        <ObjectiveProposalFlow
          product={product}
          defaultCategoryId={defaultCategoryId}
          email={storeEmail}
          requireCaptcha={false}
          pdpAuditReportId={pdpAuditReportId}
          pdpUrlScoreId={pdpUrlScoreId}
          consumerVoiceAuditId={consumerVoiceAuditId}
          aiPresenceAuditId={aiPresenceAuditId}
          intelAccessToken={intelAccessToken}
          initialProducts={initialProducts}
          initialObjectiveId={initialObjectiveId}
          recommendationIds={recommendationIds.length ? recommendationIds : undefined}
          rmn={brandSlug ? { slug: String(brandSlug) } : {}}
        />
        <ProposalDisclaimer />
      </div>
    </div>
  )
}
